import React, { useState } from 'react';
import { Calendar, Clock, Users, CheckSquare, AlertTriangle, ChevronDown } from 'lucide-react';
import { postRequest } from '../services/apiService';

const TaskCreationForm = ({ isOpen, onClose, onSubmit, tasks = [] }) => {
  const [activeTab, setActiveTab] = useState('General');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    duration: '1 day',
    percentComplete: 0,
    priority: 500,
    scheduleMode: 'auto',
    inactive: false,
    startDate: '2025-04-03T08:00',
    finishDate: '2025-04-03T17:00',
    displayOnTimeline: false,
    hideTaskBar: false,
    rollUpGantt: false,
    notes: '',
    constraintType: 'asap',
    constraintDate: '',
    deadline: '',
    calendar: 'standard',
    notifyOnStart: false,
    notifyOnComplete: false,
    notifyOnDelay: false,
    additionalRecipients: '',
    customField1: '',
    customField2: '',
    customField3: ''
  });

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    project: '',
    assignee: '',
    status: 'Pending',
    priority: 'Medium',
    startDate: '',
    endDate: '',
    delay: 0,
    type: 'Start to Start',
    precedingActivity: null,
    succeedingActivity: null,
    succeedingDelay: 0,
    succeedingStartDate: '',
    succeedingEndDate: '',
  });

  const [selectedDateOption, setSelectedDateOption] = useState('delay');
  const [succeedingDateOption, setSucceedingDateOption] = useState('delay');

  const [predecessors, setPredecessors] = useState([{
    id: '',
    type: 'Finish to Start',
    lag: 0
  }]);

  const [resources, setResources] = useState([{
    name: '',
    units: 100,
    work: 0,
    cost: 0
  }]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleNewTaskChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateStartDate = (delay) => {
    const projectStartDate = new Date('2025-03-02');
    projectStartDate.setDate(projectStartDate.getDate() + parseInt(delay));
    return projectStartDate.toISOString().split('T')[0];
  };

  const calculateEndDate = (delay) => {
    const projectStartDate = new Date('2025-03-02');
    projectStartDate.setDate(projectStartDate.getDate() + parseInt(delay) + 28);
    return projectStartDate.toISOString().split('T')[0];
  };

  const calculateSucceedingDates = (delay) => {
    const precedingTask = tasks.find((task) => task.id === newTask.precedingActivity);
    if (!precedingTask) return { startDate: '', endDate: '' };

    const precedingEndDate = new Date(precedingTask.endDate);
    const startDate = new Date(precedingEndDate);
    startDate.setDate(startDate.getDate() + parseInt(delay));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 28);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const submitTaskToAPI = async (taskData) => {
    try {
      setIsSubmitting(true);
      
      const apiData = {
        ...taskData,
        title: taskData.name
      };
      const response = await postRequest('/master/milestones/', apiData);
  
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
  
      const result = await response.json();
      console.log('Task submitted successfully:', result);
      
      return result;
    } catch (error) {
      console.error('Error submitting task:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const combinedTaskData = {
      ...formData,
      ...newTask,
      title: formData.name,
      id: Date.now(),
      startDate: selectedDateOption === 'delay' ? calculateStartDate(newTask.delay) : newTask.startDate,
      endDate: selectedDateOption === 'delay' ? calculateEndDate(newTask.delay) : newTask.endDate,
      succeedingStartDate: succeedingDateOption === 'delay' ? calculateSucceedingDates(newTask.succeedingDelay).startDate : newTask.succeedingStartDate,
      succeedingEndDate: succeedingDateOption === 'delay' ? calculateSucceedingDates(newTask.succeedingDelay).endDate : newTask.succeedingEndDate,
      predecessors: predecessors.filter(p => p.id !== ''),
      resources: resources.filter(r => r.name !== ''),
    };
    
    try {
      const result = await submitTaskToAPI(combinedTaskData);
      onSubmit(result);
      onClose();
    } catch (error) {
      console.error('Error submitting task:', error);
      onClose();
    }
  };

  const handlePredecessorChange = (index, field, value) => {
    const newPredecessors = [...predecessors];
    newPredecessors[index] = {
      ...newPredecessors[index],
      [field]: value
    };
    setPredecessors(newPredecessors);
  };

  const handleResourceChange = (index, field, value) => {
    const newResources = [...resources];
    newResources[index] = {
      ...newResources[index],
      [field]: value
    };
    setResources(newResources);
  };

  const addPredecessor = () => {
    setPredecessors([...predecessors, { id: '', type: 'Finish to Start', lag: 0 }]);
  };

  const addResource = () => {
    setResources([...resources, { name: '', units: 100, work: 0, cost: 0 }]);
  };

  const removePredecessor = (index) => {
    const newPredecessors = predecessors.filter((_, i) => i !== index);
    setPredecessors(newPredecessors);
  };

  const removeResource = (index) => {
    const newResources = resources.filter((_, i) => i !== index);
    setResources(newResources);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-[#1e3a8a] px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-white">Task Information</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
              disabled={isSubmitting}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                {['General', 'Predecessors', 'Resources', 'Advanced', 'Notes', 'Custom Fields', 'Notification'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === tab
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6">
                {activeTab === 'General' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="Task name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Duration</label>
                        <div className="mt-1 relative">
                          <input
                            type="text"
                            name="duration"
                            value={formData.duration}
                            onChange={handleChange}
                            className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 pr-8"
                            placeholder="1 day"
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center">
                            <button type="button" className="p-1 focus:outline-none text-gray-400 hover:text-gray-600">
                              <ChevronDown size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Percent complete</label>
                        <div className="mt-1 relative">
                          <div className="flex">
                            <input
                              type="number"
                              name="percentComplete"
                              value={formData.percentComplete}
                              onChange={handleChange}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="0%"
                              min="0"
                              max="100"
                            />
                            <div className="flex flex-col ml-1">
                              <button type="button" className="border border-gray-300 px-1 text-gray-500 hover:bg-gray-100 rounded-t">▲</button>
                              <button type="button" className="border border-gray-300 px-1 text-gray-500 hover:bg-gray-100 rounded-b">▼</button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                        <select
                          name="priority"
                          value={newTask.priority}
                          onChange={handleNewTaskChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Mode</label>
                      <div className="space-y-2">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="scheduleMode"
                            value="auto"
                            checked={formData.scheduleMode === 'auto'}
                            onChange={handleChange}
                            className="form-radio h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2">Auto Scheduled</span>
                        </label>
                        <label className="inline-flex items-center ml-6">
                          <input
                            type="radio"
                            name="scheduleMode"
                            value="manual"
                            checked={formData.scheduleMode === 'manual'}
                            onChange={handleChange}
                            className="form-radio h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2">Manually Scheduled</span>
                        </label>
                      </div>
                      <div className="mt-2">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name="inactive"
                            checked={formData.inactive}
                            onChange={handleChange}
                            className="form-checkbox h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2">Inactive</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Dates</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm text-gray-500">Start</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="startDate"
                              value={formData.startDate.split('T')[0]}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500">Finish</label>
                          <div className="relative">
                            <input
                              type="date"
                              name="finishDate"
                              value={formData.finishDate.split('T')[0]}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          name="displayOnTimeline"
                          checked={formData.displayOnTimeline}
                          onChange={handleChange}
                          className="form-checkbox h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Display on Timeline</span>
                      </label>
                      <div>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name="hideTaskBar"
                            checked={formData.hideTaskBar}
                            onChange={handleChange}
                            className="form-checkbox h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Hide task bar</span>
                        </label>
                      </div>
                      <div>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name="rollUpGantt"
                            checked={formData.rollUpGantt}
                            onChange={handleChange}
                            className="form-checkbox h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Roll up Gantt Bar to Summary</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'Predecessors' && (
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-medium">Predecessor Tasks</h3>
                      <button
                        type="button"
                        onClick={addPredecessor}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Add Predecessor
                      </button>
                    </div>
                    <div className="space-y-4">
                      {predecessors.map((predecessor, index) => (
                        <div key={index} className="grid grid-cols-4 gap-4 items-center">
                          <div>
                            <select
                              value={predecessor.id}
                              onChange={(e) => handlePredecessorChange(index, 'id', e.target.value)}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select Task</option>
                              {tasks.map(task => (
                                <option key={task.id} value={task.id}>{task.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <select
                              value={predecessor.type}
                              onChange={(e) => handlePredecessorChange(index, 'type', e.target.value)}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="Finish to Start">Finish to Start</option>
                              <option value="Start to Start">Start to Start</option>
                              <option value="Finish to Finish">Finish to Finish</option>
                              <option value="Start to Finish">Start to Finish</option>
                            </select>
                          </div>
                          <div>
                            <input
                              type="number"
                              value={predecessor.lag}
                              onChange={(e) => handlePredecessorChange(index, 'lag', parseInt(e.target.value))}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="Lag (days)"
                            />
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => removePredecessor(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'Resources' && (
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-medium">Assigned Resources</h3>
                      <button
                        type="button"
                        onClick={addResource}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Add Resource
                      </button>
                    </div>
                    <div className="space-y-4">
                      {resources.map((resource, index) => (
                        <div key={index} className="grid grid-cols-5 gap-4 items-center">
                          <div>
                            <select
                              value={resource.name}
                              onChange={(e) => handleResourceChange(index, 'name', e.target.value)}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select Resource</option>
                              <option value="Lt. Kumar">Lt. Kumar</option>
                              <option value="Lt. Sharma">Lt. Sharma</option>
                              <option value="Lt. Patel">Lt. Patel</option>
                              <option value="Cdr. Singh">Cdr. Singh</option>
                            </select>
                          </div>
                          <div>
                            <input
                              type="number"
                              value={resource.units}
                              onChange={(e) => handleResourceChange(index, 'units', parseInt(e.target.value))}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="Units (%)"
                              min="0"
                              max="100"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={resource.work}
                              onChange={(e) => handleResourceChange(index, 'work', parseInt(e.target.value))}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="Work (hours)"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={resource.cost}
                              onChange={(e) => handleResourceChange(index, 'cost', parseInt(e.target.value))}
                              className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="Cost"
                            />
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => removeResource(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'Advanced' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Constraint Type</label>
                        <select
                          name="constraintType"
                          value={formData.constraintType}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="asap">As Soon As Possible</option>
                          <option value="alap">As Late As Possible</option>
                          <option value="mss">Must Start On</option>
                          <option value="mfs">Must Finish On</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Constraint Date</label>
                        <input
                          type="date"
                          name="constraintDate"
                          value={formData.constraintDate}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Deadline</label>
                      <input
                        type="date"
                        name="deadline"
                        value={formData.deadline}
                        onChange={handleChange}
                        className="mt-1 block w-full md:w-1/2 rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Calendar</label>
                      <select
                        name="calendar"
                        value={formData.calendar}
                        onChange={handleChange}
                        className="mt-1 block w-full md:w-1/2 rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="standard">Standard (Project Calendar)</option>
                        <option value="24hour">24 Hours</option>
                        <option value="night">Night Shift</option>
                      </select>
                    </div>
                  </div>
                )}

                {activeTab === 'Notes' && (
                  <div className="py-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Task Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="8"
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Add notes about this task..."
                    ></textarea>
                  </div>
                )}

                {activeTab === 'Custom Fields' && (
                  <div className="py-4 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Field 1</label>
                      <input
                        type="text"
                        name="customField1"
                        value={formData.customField1}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Field 2</label>
                      <input
                        type="text"
                        name="customField2"
                        value={formData.customField2}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Field 3</label>
                      <select
                        name="customField3"
                        value={formData.customField3}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select a value</option>
                        <option value="option1">Option 1</option>
                        <option value="option2">Option 2</option>
                        <option value="option3">Option 3</option>
                      </select>
                    </div>
                  </div>
                )}

                {activeTab === 'Notification' && (
                  <div className="py-4 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notification Settings</label>
                      <div className="space-y-2">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name="notifyOnStart"
                            checked={formData.notifyOnStart}
                            onChange={handleChange}
                            className="form-checkbox h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Notify team when task starts</span>
                        </label>
                        <div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name="notifyOnComplete"
                              checked={formData.notifyOnComplete}
                              onChange={handleChange}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            <span className="ml-2 text-sm text-gray-700">Notify team when task completes</span>
                          </label>
                        </div>
                        <div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name="notifyOnDelay"
                              checked={formData.notifyOnDelay}
                              onChange={handleChange}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            <span className="ml-2 text-sm text-gray-700">Notify team when task is delayed</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Additional Recipients</label>
                      <input
                        type="text"
                        name="additionalRecipients"
                        value={formData.additionalRecipients}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        placeholder="Email addresses (separated by comma)"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-3 flex justify-between items-center border-t border-gray-200">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1e3a8a]"
                  >
                    OK
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCreationForm;